import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import Bare from '../components/Bare';
import { analyticsAPI, productionAPI } from '../api';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function effColor(eff) {
  if (!eff || eff === 0) return 'var(--dim)';
  if (eff >= 80) return '#22c55e';
  if (eff >= 50) return '#f59e0b';
  return '#ef4444';
}

function effBg(eff) {
  if (!eff || eff === 0) return 'transparent';
  if (eff >= 80) return '#22c55e14';
  if (eff >= 50) return '#f59e0b14';
  return '#ef444414';
}

function effLabel(eff) {
  if (!eff || eff === 0) return 'Нет данных';
  if (eff >= 80) return 'Норма ✓';
  if (eff >= 50) return 'Ниже нормы';
  return 'Критично';
}

// ── SVG Bar Chart ─────────────────────────────────────────────────────────────

function EfficiencyChart({ intervals, intervalMin }) {
  if (!intervals || intervals.length === 0) return null;

  const H       = 180;
  const BAR_GAP = 2;
  const MAX_EFF = 150;  // bars capped at 150% for display

  // Подписаны были каждая восьмая точка: при часовом шаге на оси стояли
  // 00:00, 03:00, 06:00 — а столбиков между ними три, и какой из них какой
  // час, приходилось отсчитывать пальцем. Теперь подписана КАЖДАЯ точка
  // выбранного интервала, а слот под столбик не уже подписи: 34 пикселя —
  // ширина «00:00» девятым кеглем. Не влезает в экран — график едет вбок,
  // для того он и в прокручиваемой обёртке.
  const LABEL_W = 34;
  const barW    = Math.max(6, Math.min(32,
    Math.floor((720 - BAR_GAP * intervals.length) / intervals.length)));
  const slot    = Math.max(barW + BAR_GAP, LABEL_W);
  const totalW  = intervals.length * slot;

  const y100 = H * (1 - 100 / MAX_EFF);
  const y50  = H * (1 - 50  / MAX_EFF);

  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 2 }}>
      <svg width={Math.max(totalW, 300)} height={H + 28} style={{ display: 'block' }}>

        {/* 50% guide */}
        <line x1={0} x2={totalW} y1={y50} y2={y50}
          stroke="var(--border)" strokeWidth={1} opacity={0.5} />
        <text x={4} y={y50 - 3} fontSize={8} fill="var(--dim)" opacity={0.6}>50%</text>

        {/* 100% norm line */}
        <line x1={0} x2={totalW} y1={y100} y2={y100}
          stroke="#ef4444" strokeDasharray="5,3" strokeWidth={1.5} opacity={0.7} />
        <text x={4} y={y100 - 3} fontSize={9} fill="#ef4444" fontWeight="700">100%</text>

        {intervals.map((iv, i) => {
          const eff    = iv.efficiency ?? 0;
          const capped = Math.min(eff, MAX_EFF);
          const barH   = (capped / MAX_EFF) * H;
          // Столбик по центру слота: подпись стоит под ним, а не сбоку
          const x      = i * slot + (slot - barW) / 2;

          const color =
            eff === 0 ? 'var(--border)' :
            eff >= 80 ? '#22c55e' :
            eff >= 50 ? '#f59e0b' :
                        '#ef4444';

          return (
            <g key={i}>
              <rect
                x={x}
                y={H - Math.max(barH, eff > 0 ? 2 : 0)}
                width={barW}
                height={Math.max(barH, eff > 0 ? 2 : 0)}
                fill={color}
                rx={barW > 5 ? 2 : 1}
                opacity={eff === 0 ? 0.2 : 0.85}
              >
                <title>{iv.time} — {iv.count} листов ({eff}% от нормы {iv.norm} л/{intervalMin}мин)</title>
              </rect>

              <text x={x + barW / 2} y={H + 17}
                textAnchor="middle" fontSize={9.5} fill="var(--dim)">
                {iv.time}
              </text>
            </g>
          );
        })}
      </svg>

      <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 11, color: 'var(--dim)' }}>
        {[
          { color: '#22c55e', label: '≥80% — норма' },
          { color: '#f59e0b', label: '50–79%' },
          { color: '#ef4444', label: '<50% — критично' },
        ].map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Press Card ────────────────────────────────────────────────────────────────

function PressCard({ press }) {
  const eff    = press.avg_efficiency;
  const color  = effColor(eff);
  const total  = press.total_count;
  const norm8h = press.norm_per_hour * 8;
  const norm24 = press.norm_per_hour * 24;
  const pctDay = norm24 > 0 ? Math.round(total / norm24 * 100) : 0;

  return (
    <div style={{
      background: 'var(--card)',
      borderRadius: 14,
      border: '1px solid var(--border)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: effBg(eff),
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--text)' }}>
            🏭 {press.press_name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 3 }}>
            Норма: <strong>{press.norm_per_hour}</strong> л/час
            {' · '}
            За интервал: <strong>{press.norm_per_interval}</strong> л/{press.interval_minutes} мин
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1 }}>
            {eff > 0 ? `${eff}%` : '—'}
          </div>
          <div style={{
            fontSize: 11, fontWeight: 700, color,
            background: eff > 0 ? `${color}22` : 'var(--bg)',
            borderRadius: 6, padding: '2px 10px',
            display: 'inline-block', marginTop: 4,
          }}>
            {effLabel(eff)}
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        borderBottom: '1px solid var(--border)',
      }}>
        {[
          {
            label: 'Листов за день',
            value: total.toLocaleString('ru'),
            sub:   `${pctDay}% от суточной нормы`,
          },
          {
            label: 'Норма смены (8ч)',
            value: norm8h.toLocaleString('ru'),
            sub:   'листов',
          },
          {
            label: 'Норма суток (24ч)',
            value: norm24.toLocaleString('ru'),
            sub:   'листов',
          },
          {
            label: 'Операторов',
            value: press.operators?.length || 0,
            sub:   press.operators?.slice(0, 2).map(o => o.name).join(', ') +
                   (press.operators?.length > 2 ? '...' : '') || '—',
          },
        ].map((s, i) => (
          <div key={i} style={{
            padding: '10px 16px',
            borderRight: i < 3 ? '1px solid var(--border)' : 'none',
          }}>
            <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 600, marginBottom: 4 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>
              {s.value}
            </div>
            <div style={{
              fontSize: 11, color: 'var(--muted)', marginTop: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {s.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ padding: '14px 20px 10px' }}>
        <div style={{ fontSize: 12, color: 'var(--dim)', fontWeight: 600, marginBottom: 8 }}>
          Эффективность по интервалам (% от нормы за интервал)
        </div>

        {total === 0 ? (
          <div style={{
            padding: '28px 0', textAlign: 'center',
            color: 'var(--dim)', fontSize: 13,
            background: 'var(--bg)', borderRadius: 8,
          }}>
            Нет данных за выбранную дату
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              Данные появятся когда операторы нажмут +1
            </div>
          </div>
        ) : (
          <EfficiencyChart intervals={press.intervals} intervalMin={press.interval_minutes} />
        )}
      </div>

      {/* Operators */}
      {press.operators && press.operators.length > 0 && (
        <div style={{ padding: '4px 20px 14px' }}>
          <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
            Операторы
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {press.operators.map((op, i) => (
              <div key={i} style={{
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 20,
                padding: '4px 12px',
                fontSize: 12,
                color: 'var(--muted)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {op.name}
                <span style={{
                  fontWeight: 800, color: 'var(--text)',
                  background: 'var(--card)', borderRadius: 10,
                  padding: '0 6px', fontSize: 11,
                }}>
                  {op.count} л
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const INTERVALS = [
  { key: '15m', label: '15 мин', desc: '96 точек/день' },
  { key: '30m', label: '30 мин', desc: '48 точек/день' },
  { key: '1h',  label: '1 час',  desc: '24 точки/день' },
];

export default function PressEfficiencyPage({ bare, onBack }) {
  // Внутри общего дашборда директора страница рисуется без своей шапки и меню
  const Shell = bare ? Bare : Layout;

  const [effData,  setEffData]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [groupBy,  setGroupBy]  = useState('1h');
  const [date,     setDate]     = useState(todayStr());
  const [pressId,  setPressId]  = useState('');
  const [presses,  setPresses]  = useState([]);

  useEffect(() => {
    productionAPI.getPresses().then(r => setPresses(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy, date };
      if (pressId) params.press_id = pressId;
      const r = await analyticsAPI.getEfficiency(params);
      setEffData(r.data);
    } catch {
      toast.error(t("Ошибка загрузки данных"));
    } finally {
      setLoading(false);
    }
  }, [groupBy, date, pressId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pressList   = effData?.presses || [];
  const avgEff      = pressList.length
    ? Math.round(pressList.reduce((s, p) => s + (p.avg_efficiency || 0), 0) / pressList.length)
    : 0;
  const totalSheets = pressList.reduce((s, p) => s + p.total_count, 0);
  const activeCount = pressList.filter(p => p.total_count > 0).length;

  return (
    <Shell title={t("Дашборд директора")}>

      {/* Крупной строкой сверху, а не стрелкой в углу: сюда попадают одной
          кнопкой из дашборда, и в меню слева этого раздела нет — уйти отсюда
          иначе нечем. */}
      {onBack && (
        <button onClick={onBack} style={{
          display: 'block', width: '100%', textAlign: 'left', marginBottom: 14,
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '11px 16px', cursor: 'pointer',
          color: 'var(--text)', fontSize: 14, fontWeight: 700,
          fontFamily: 'inherit',
        }}>
          ← {t('Назад к прессам (OEE)')}
        </button>
      )}

      {/* Controls */}
      <div style={{
        background: 'var(--card)',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 20,
        border: '1px solid var(--border)',
        display: 'flex',
        gap: 20,
        alignItems: 'flex-end',
        flexWrap: 'wrap',
      }}>

        {/* Date */}
        <div>
          <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
            Дата
          </div>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--input)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              color: 'var(--text)',
              fontSize: 14,
              outline: 'none',
            }}
          />
        </div>

        {/* Interval */}
        <div>
          {/* Подсказка «24 точки/день» стояла отдельной строкой ПОД
              кнопками, и этот блок оказывался на строку выше соседних: ряд
              разъезжался. Она ушла в заголовок — там же, где стоит, и ничего
              не сдвигает. */}
          <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
            Интервал
            <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6 }}>
              · {INTERVALS.find(o => o.key === groupBy)?.desc}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {INTERVALS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setGroupBy(opt.key)}
                title={opt.desc}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: groupBy === opt.key ? 'var(--accent)' : 'var(--bg)',
                  color:      groupBy === opt.key ? '#fff'     : 'var(--muted)',
                  border:     `1px solid ${groupBy === opt.key ? 'var(--accent)' : 'var(--border)'}`,
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Press filter */}
        <div>
          <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
            Пресс
          </div>
          <select
            value={pressId}
            onChange={e => setPressId(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'var(--input)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              color: 'var(--text)',
              fontSize: 14,
              outline: 'none',
            }}
          >
            <option value="">{t('Все прессы')}</option>
            {presses.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            padding: '9px 20px',
            background: loading ? 'var(--bg)' : 'var(--accent)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            color: loading ? 'var(--dim)' : '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? '...' : '↻ Обновить'}
        </button>

        {/* Summary KPIs */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 28 }}>
          {[
            { label: 'Ср. эффективность', value: avgEff > 0 ? `${avgEff}%` : '—', color: effColor(avgEff) },
            { label: 'Листов за день',    value: totalSheets > 0 ? totalSheets.toLocaleString('ru') : '—', color: 'var(--text)' },
            { label: 'Активных прессов',  value: `${activeCount} / ${pressList.length}`, color: activeCount > 0 ? '#22c55e' : 'var(--dim)' },
          ].map((kpi, i) => (
            <div key={i} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase' }}>
                {kpi.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, color: kpi.color, lineHeight: 1.2 }}>
                {kpi.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Press cards */}
      {loading && pressList.length === 0 ? (
        <div style={{
          background: 'var(--card)', borderRadius: 12, padding: 60,
          textAlign: 'center', color: 'var(--dim)', border: '1px solid var(--border)',
        }}>
          Загрузка...
        </div>
      ) : pressList.length === 0 ? (
        <div style={{
          background: 'var(--card)', borderRadius: 12, padding: 60,
          textAlign: 'center', color: 'var(--dim)', border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{t('Нет данных о прессах')}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {pressList.map(press => (
            <PressCard key={press.press_id} press={press} />
          ))}
        </div>
      )}
    </Shell>
  );
}
